import { clients } from '../lib/https';
import { ConfigurationSettings } from '../models/configuration-settings';
import { useAuthStore } from 'state/store/auth/useAuthStore';

import { FileResponse } from '/types/storage-type';
import { VectorUploadItem } from '/features/chat/types/chat';

const FILE_BLOB_CACHE_TTL_MS = 5 * 60 * 1000;
const fileBlobUrlCache = new Map<string, { url: string; expiresAt: number }>();
const fileBlobInFlight = new Map<string, Promise<string | undefined>>();

export const uploadFilesToVectorStorage = async (
  items: VectorUploadItem[],
  model_name: string,
  config: ConfigurationSettings
) => {
  const params = new URLSearchParams({
    model_name: model_name,
  });
  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.uploadAll}?${params.toString()}`;
  const body = items.map((item) => ({ file_id: item.fileId }));

  return clients.post(url, config.blocksKey, JSON.stringify(body));
};

export const deleteConversationById = async (
  conversationId: string,
  config: ConfigurationSettings
) => {
  const baseUrl = `${config.paths.seliseAiBaseUrl}`;
  const url = `${baseUrl}${config.paths.seliseAi.collaborationConversation}/${conversationId}`;
  return clients.delete(url, config.blocksKey);
};

const buildGetFileUrl = (
  fileId: string,
  config: ConfigurationSettings,
  version?: number
): string => {
  const params = new URLSearchParams({
    FileId: fileId,
    ProjectKey: config.blocksKey,
  });

  if (version !== undefined) {
    params.append('Version', version.toString());
  }

  const baseUrl = `https://${config.paths.baseUrl}`;
  return `${baseUrl}${config.paths.storage.getFile}?${params.toString()}`;
};

const createFileAuthHeaders = (config: ConfigurationSettings): HeadersInit => {
  const authToken = useAuthStore.getState().accessToken;
  return {
    'x-blocks-key': config.blocksKey,
    ...(authToken ? { Authorization: `bearer ${authToken}` } : {}),
  };
};


export const getFile = (fileId: string, config: ConfigurationSettings, version?: number) => {
  const url = buildGetFileUrl(fileId, config, version);
  return clients.get<FileResponse>(url, config.blocksKey);
};

/**
 * Azure Blob Storage URLs (both public and SAS URLs) do not use app credentials.
 * Fetching with `credentials: 'include'` causes CORS failures when Azure responds
 * with wildcard `Access-Control-Allow-Origin: *`. Always omit credentials here.
 */
export const getAuthorizedBlobUrl = async (
  url: string,
  _config?: ConfigurationSettings
) => {
  const blobResponse = await fetch(url, {
    credentials: 'omit',
  });
  if (!blobResponse.ok) {
    throw new Error(`File download failed with status ${blobResponse.status}`);
  }

  const fileBlob = await blobResponse.blob();
  if (fileBlob.size === 0) {
    return undefined;
  }

  return URL.createObjectURL(fileBlob);
};

export interface FileOpenResult {
  blobUrl?: string;
  fileName?: string;
}

export interface SourceFileOpenResult {
  openUrl?: string;
  fileName?: string;
}

const isDirectBinaryContentType = (contentType: string) =>
  contentType.includes('application/pdf') ||
  contentType.includes('application/octet-stream') ||
  contentType.includes('image/') ||
  contentType.includes('officedocument') ||
  contentType.includes('msword') ||
  contentType.includes('ms-excel') ||
  contentType.includes('ms-powerpoint') ||
  contentType.includes('application/vnd.ms-') ||
  contentType.includes('text/csv') ||
  contentType.includes('application/csv');

/**
 * Resolves a source-file link for opening in a new browser tab.
 * Returns the storage SAS URL (or get-file API URL) directly — does not fetch the blob.
 */
export const resolveSourceFileOpenUrl = async (
  fileId: string,
  config: ConfigurationSettings,
  version?: number
): Promise<SourceFileOpenResult> => {
  const url = buildGetFileUrl(fileId, config, version);
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createFileAuthHeaders(config),
  });

  if (response.status === 204 || response.status === 404) {
    return {};
  }

  if (!response.ok) {
    throw new Error(`File request failed with status ${response.status}`);
  }

  const contentType = response.headers.get('Content-Type') || '';
  if (isDirectBinaryContentType(contentType)) {
    // Same-origin get-file URL works for window.open with cookies; not for external embeds.
    return { openUrl: url };
  }

  const responseText = await response.text();
  const finalResponseText = responseText.trim() === '' ? '{}' : responseText;
  let fileResponse: FileResponse;
  try {
    fileResponse = JSON.parse(finalResponseText) as FileResponse;
  } catch {
    return {};
  }

  if (!fileResponse?.isSuccess || !fileResponse.url) {
    return { fileName: fileResponse?.name };
  }

  return { openUrl: fileResponse.url, fileName: fileResponse.name };
};

/**
 * Resolves a file for in-app preview/download. Uses a raw fetch so 204 / binary
 * responses are handled correctly (clients.get treats empty 204 bodies as `{}`).
 */
export const resolveFileForOpen = async (
  fileId: string,
  config: ConfigurationSettings,
  version?: number
): Promise<FileOpenResult> => {
  const url = buildGetFileUrl(fileId, config, version);
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createFileAuthHeaders(config),
  });

  if (response.status === 204 || response.status === 404) {
    return {};
  }

  if (!response.ok) {
    throw new Error(`File request failed with status ${response.status}`);
  }

  const contentType = response.headers.get('Content-Type') || '';
  if (isDirectBinaryContentType(contentType)) {
    const fileBlob = await response.blob();
    if (fileBlob.size === 0) {
      return {};
    }
    return { blobUrl: URL.createObjectURL(fileBlob) };
  }

  const responseText = await response.text();
  const finalResponseText = responseText.trim() === '' ? '{}' : responseText;
  let fileResponse: FileResponse;
  try {
    fileResponse = JSON.parse(finalResponseText) as FileResponse;
  } catch {
    return {};
  }

  if (!fileResponse?.isSuccess || !fileResponse.url) {
    return { fileName: fileResponse?.name };
  }

  const blobUrl = await getAuthorizedBlobUrl(fileResponse.url, config);
  return { blobUrl, fileName: fileResponse.name };
};

export const getFileBlobUrl = async (
  fileId: string,
  config: ConfigurationSettings,
  version?: number
) => {
  const { blobUrl } = await resolveFileForOpen(fileId, config, version);
  return blobUrl;
};

export const openPdfInNewTab = async (
  fileId: string,
  config: ConfigurationSettings,
  version?: number
) => {
  const tab = window.open('about:blank', '_blank');

  try {
    const blobUrl = await getFileBlobUrl(fileId, config, version);
    if (!blobUrl) {
      tab?.close();
      return;
    }

    const blob = await fetch(blobUrl).then((response) => response.blob());
    const pdfUrl = URL.createObjectURL(
      new File([await blob.arrayBuffer()], 'document.pdf', { type: 'application/pdf' })
    );

    if (!tab || tab.closed) {
      window.open(pdfUrl, '_blank');
      return;
    }

    tab.document.open();
    tab.document.write(
      `<!DOCTYPE html><html><head><title>PDF</title><style>html,body,iframe{margin:0;height:100%;width:100%;border:0}</style></head><body><iframe src="${pdfUrl}" type="application/pdf"></iframe></body></html>`
    );
    tab.document.close();
  } catch (error) {
    tab?.close();
    throw error;
  }
};

export const downloadFileById = async (
  fileId: string,
  fileName: string,
  config: ConfigurationSettings,
  version?: number
) => {
  const blobUrl = await getFileBlobUrl(fileId, config, version);
  if (!blobUrl) return false;

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  return true;
};
